import { Field, ErrorMessage } from "formik";
import { get, set } from "lodash";
import { IMAGE_URL } from "../../config";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import { useRef, useState } from "react";

export const FormFileUploadField = ({
  label = "Upload File",
  name = "fileField",
  labelClassName = "",
  className = "",
  previewClassName = "",
  accept = "*",
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState("");

  const inputRef = useRef(null);

  const uploadFile = (file, form, name) => {
    if (!file) return;

    setUploadingFileName(file.name);
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onload = () => {
      setIsUploading(false);
      setUploadingFileName("");

      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        const filePath = response?.data?.filePath;

        const fullUrl = filePath.startsWith("http")
          ? filePath
          : `https://api-new.requal.in${filePath}`;

        const updatedValues = { ...form.values };
        updatedValues[name] = {
          url: fullUrl,
          name: file.name,
        };

        form.setValues(updatedValues);
      } else {
        alert("File upload failed!");
      }
    };

    xhr.onerror = () => {
      setIsUploading(false);
      setUploadingFileName("");
      alert("An error occurred during upload!");
    };

    xhr.send(formData);
  };

  const handleFileSelect = (event, form, name) => {
    const file = event.target.files[0];
    uploadFile(file, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files[0];
    uploadFile(file, form, name);
  };

  const handleRemoveFile = (form, name) => {
    const updatedValues = { ...form.values };
    updatedValues[name] = null;
    form.setValues(updatedValues);
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const fileData = get(form.values, name);
        const error = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-2 w-full ${className}`}>
            {label && (
              <label
                className={`text-fs_14 md:text-fs_16 font-fw_400 capitalize ${
                  error ? "text-errorColor" : labelClassName
                }`}
              >
                {formatUnderscoreToSpace(label)}
              </label>
            )}
            {fileData && !isUploading ? (
              <div
                className={`border p-3 rounded-[8px] flex items-center justify-between w-full max-w-[30rem] bg-[#EDF4F94D] ${previewClassName}`}
              >
                <span className="text-sm text-black truncate">
                  {fileData.name}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(form, name)}
                  className="ml-2 text-xs text-white bg-red-500 hover:bg-red-600 rounded px-2 py-0.5"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div
                className={`border-[1px] rounded-lg p-6 text-center transition-all relative overflow-hidden cursor-pointer h-[200px]
                flex justify-center items-center ${
                  isDragActive
                    ? "border-primary bg-purple-50"
                    : "border-gray-300 bg-gray-50 hover:border-primary"
                }
                ${error ? "border-errorColor" : ""}
                `}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => handleDrop(e, form, name)}
                onClick={() => inputRef.current.click()}
              >
                {/* ===== Uploading ===== */}
                {isUploading ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium text-gray-700 truncate">
                      Uploading: {uploadingFileName}
                    </p>

                    <div className="w-full bg-gray-200 rounded-full h-[6px] overflow-hidden">
                      <div
                        className="h-full transition-all duration-200"
                        style={{
                          width: `${uploadProgress}%`,
                          background:
                            "linear-gradient(to right, #00e676, #1e9d43)",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8 text-gray-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 
                              2.25 0 0021 18.75V16.5m-3-6l-6-6m0 
                              0l-6 6m6-6V15"
                      />
                    </svg>
                    <p className="text-gray-600 text-sm">
                      Drag & drop file or{" "}
                      <span className="text-primary font-medium">Browse</span>
                    </p>
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept={accept}
                  className="hidden"
                  onChange={(event) => handleFileSelect(event, form, name)}
                />
              </div>
            )}

            <span className="text-errorColor text-fs_14 h-[1rem]">
              <ErrorMessage name={name} />
            </span>
          </div>
        );
      }}
    </Field>
  );
};
